// Tz-naive by convention, like `DateTimeField`: the value's *local* calendar
// fields are the canonical picked date, and the submit boundary re-anchors them
// to the project tz (`pickerDateToProjectNoonMs` in @utils/dateUtils). A
// `timeZone` here would re-read the epoch through a fixed zone and disagree with
// the field's own selection logic for any browser outside it.
const formatter = new Intl.DateTimeFormat('ja-JP', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** How a DateField writes a picked day (yyyy/mm/dd). Copy that quotes a day
 *  next to these fields uses it too, so one form never shows two formats. */
export function formatFieldDate(date: Date): string {
  return formatter.format(date);
}

/* The API speaks calendar days ('YYYY-MM-DD'), never instants; DateField
   speaks Date. Both ends of that conversion stay tz-naive — the picked day
   lives in the Date's LOCAL fields — which is the picker convention
   documented on `pickerDateToProjectNoonMs` in utils/dateUtils.ts. Going
   through UTC here (`new Date('2026-08-01')`) would land the calendar on the
   wrong day for anyone west of Greenwich. */
export function ymdToDate(ymd: string): Date | null {
  if (!ymd) return null;
  const [y, m, d] = ymd.split('-').map(Number);
  return y && m && d ? new Date(y, m - 1, d) : null;
}

export function dateToYmd(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** A 'YYYY-MM-DD' day as the date fields show it. Text that is not a day is
 *  returned as it came rather than inventing one. */
export function formatFieldDay(ymd: string): string {
  const date = ymdToDate(ymd);
  return date ? formatFieldDate(date) : ymd;
}
